import { router } from "@inertiajs/react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogTrigger,
  Modal,
  ModalOverlay,
} from "@/components/ui/modal"
import { addSchoolAdminCallListPath, adminCallListPath } from "@/routes"

import type { SchoolCandidate } from "../types"

/** Bring a mirrored school into this scope by hand: search by name, pick one. */
export function AddSchoolModal({
  scopeId,
  candidates,
}: {
  scopeId: number
  candidates: SchoolCandidate[]
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")

  const search = () =>
    router.visit(adminCallListPath(scopeId), {
      data: { q: query },
      preserveState: true,
      preserveScroll: true,
      only: ["school_candidates"],
    })

  const add = (salesforceAccountId: string) =>
    router.post(
      addSchoolAdminCallListPath(scopeId),
      { salesforce_account_id: salesforceAccountId },
      { preserveScroll: true, onSuccess: () => setOpen(false) },
    )

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button size="md" color="secondary">
        Add a school
      </Button>
      <ModalOverlay isDismissable>
        <Modal>
          <Dialog className="bg-primary mx-auto mt-20 flex w-full max-w-lg flex-col gap-5 rounded-xl p-6 shadow-xl md:mt-0">
            <div className="flex flex-col gap-1">
              <h3 className="text-primary text-lg font-semibold">
                Add a school to this list
              </h3>
              <p className="text-tertiary text-sm">
                Any school in the mirror. It stays on this scope's list until
                removed.
              </p>
            </div>
            <div className="flex items-end gap-2">
              <Input
                aria-label="School name"
                placeholder="Search by school name…"
                size="sm"
                className="flex-1"
                value={query}
                onChange={(value: string) => setQuery(value)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter") search()
                }}
              />
              <Button size="sm" color="secondary" onPress={search}>
                Search
              </Button>
            </div>
            {candidates.length > 0 ? (
              <ul className="divide-secondary flex flex-col divide-y">
                {candidates.map((c) => (
                  <li
                    key={c.salesforce_account_id}
                    className="flex items-center justify-between gap-3 py-2"
                  >
                    <div className="flex flex-col">
                      <span className="text-primary text-sm font-medium">
                        {c.name}
                      </span>
                      <span className="text-tertiary text-xs">
                        {c.region ?? "—"}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      color="primary"
                      onPress={() => add(c.salesforce_account_id)}
                    >
                      Add
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-tertiary text-sm">
                Search for a school to add.
              </p>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  )
}
